<script setup lang="ts">
import type { ContentItem } from '~/components/ContentCard.vue'
import type { CardProps } from '~/utils/card'
import { faArrowRight } from '@fortawesome/pro-light-svg-icons'

const props = defineProps<Omit<CardProps, 'type'> & {
  doc: ContentItem
}>()

const doc = computed(() => props.doc)

const { isFavorite, toggleFavorite } = useFavorites()

const { $parseStringToDocument } = useNuxtApp()

function extractFirstParagraphText(html: string | undefined): string {
  if (!html) {
    return ''
  }

  const MAX_CHARS = 300

  try {
    const doc = $parseStringToDocument(html, ['process-links'])
    const bodyTextDiv = doc.body.querySelector('.body-text')
    const firstP = bodyTextDiv?.querySelector('p') || doc.body.querySelector('p')

    if (!firstP) {
      return doc.body.textContent?.trim().slice(0, MAX_CHARS) || ''
    }

    const text = firstP.textContent?.trim() || ''
    return text.slice(0, MAX_CHARS)
  }
  catch (error) {
    console.error('Failed to extract paragraph text:', error)
    return ''
  }
}

const url = useContentUrl(doc)
</script>

<template>
  <NuxtLink :tabindex="0" :to="url" class="group academic-article-card relative bg-surface-primary group no-underline flex shrink-0 flex-col z-10 gap-2.5 pt-xl h-[419px] [--card-padding:theme(spacing.l)] pb-[--card-padding] px-[--card-padding] rounded-4xl hover:bg-surface-hover cursor-pointer">
    <div class="flex flex-col gap-l flex-grow">
      <div class="flex items-start gap-4xs justify-between">
        <h5 class="break-words line-clamp-3" :class="cardVariants.title({ type: 'article' })" v-text="doc.title" />
        <FavoriteButton v-if="doc.remoteId" class="-mt-s shrink-0" :selected="isFavorite('literature', doc.remoteId)" :aria-label="isFavorite('literature', doc.remoteId) ? `Fjern ${doc.title} fra favoritter` : `Legg til ${doc.title} i favoritter`" @click.prevent="toggleFavorite('literature', doc.remoteId)" />
      </div>
      <p v-if="doc.bodytext" class="text-xsmall text-primary line-clamp-4">
        {{ extractFirstParagraphText(doc.bodytext) }}
      </p>
    </div>

    <div class="flex flex-col gap-3xs text-caption pr-2xl">
      <span v-if="doc.date" class="text-secondary">
        Sist oppdatert {{ formatDate(new Date(doc.date)) }}
      </span>
      <span v-if="doc.authors?.length" class="text-secondary flex flex-wrap gap-3xs after:content-[','] last:after:content-['']">
        Av&nbsp;{{ doc.authors }}
      </span>
    </div>

    <div data-navigation-button-wrapper>
      <Button class="!bg-transparent !p-0 group-hover:translate-x-1 transition-transform focus-visible:!ring-offset-0 focus-visible:!ring-0" :aria-label="`Les fagartikkel${doc.title}`" @click.prevent="navigateTo(url)">
        <Shape variant="hexagon" :icon="faArrowRight" scale="0.8" class="w-[70px] text-tag-default fill-moss-500" />
      </Button>
    </div>
  </NuxtLink>
</template>

<style scoped lang='postcss'>
.academic-article-card {
  --card-container-bg: theme('colors.blurple.100');
}

[data-navigation-button-wrapper] {
  --padding: theme(spacing.5);
  --padding-top: theme(spacing.7);
  --padding-right: theme(spacing.2);
  --padding-bottom: theme(spacing.2);
  --roundness: theme('borderRadius.4xl');
  --size: theme(size.14);
  --shadow-offset: calc(var(--size) / 2);
  --negative-size: calc(var(--size) * -1);
  --negative-position: theme(spacing[0.5]);
  transition:
    border-radius 0.2s,
    padding-bottom 0.2s;

  @apply absolute -bottom-[--negative-position] -right-[--negative-position] pb-[--padding-bottom] pt-[--padding-top] pl-[--padding] pr-[--padding-right] rounded-tl-[--roundness];
  background: var(--card-container-bg);

  &:before,
  &:after,
  button:before {
    @apply content-[''] absolute size-[--size] z-0;
    transition: border-color 0.2s;
  }

  button:before {
    @apply right-0 rounded-tr-[--roundness] bottom-[--negative-size];
  }

  &:before,
  &:after {
    box-shadow: 0 var(--shadow-offset) 0 0 var(--card-container-bg);
  }

  &:before {
    @apply rounded-br-[--roundness] bottom-0 left-[--negative-size];
  }

  &:after {
    @apply rounded-br-[--roundness] right-0 top-[--negative-size];
  }
}

/* Focus styles */
.academic-article-card:focus {
  @apply outline-none;
  box-shadow: 0 0 0 2px theme('colors.blurple.300');
}

.academic-article-card:focus [data-navigation-button-wrapper] {
  @apply border-l-2 border-t-2 border-border-primary;
}

.academic-article-card:focus [data-navigation-button-wrapper]:before {
  @apply border-r-2 border-b-2 border-border-primary;
}

.academic-article-card:focus [data-navigation-button-wrapper]:after {
  @apply border-r-2 border-b-2 border-border-primary;
}
</style>
